import Content, { ContentNS } from "content/content";
import usePaxSession from "features/shared/hooks/usePaxSession";
import ChecklistResolutionStatus from "models/checklist/checklist-resolution-status";
import { useAppStore } from "store/store";
import { normalizeName } from "utils";

import {
  PaxCheckDocumentProvisionMethod,
  PaxCheckIdMismatches,
  PaxCheckRecognizeDocumentError,
  PaxCheckRulesMismatch,
} from "api/models";
import AllowedDocument from "models/documents/allowed-document";
import {
  ApplyDocumentErrorIdMismatch,
  ApplyDocumentErrorNoApplicableItems,
  ApplyDocumentErrorProvidePassportFirst,
  ApplyDocumentErrorRecognitionFailed,
  ApplyDocumentErrorRulesMismatch,
} from "services/data-provider/ApplyDocumentResponse";

const MB = 1024 ** 2;
const GB = 1024 ** 3;

const documentTitle = (docs: AllowedDocument[] | undefined) => {
  if (docs === undefined || docs.length === 0) {
    return Content.shared
      .t("DOCUMENT.name", { ns: ContentNS.docs })
      .toLowerCase();
  }
  return docs
    .map((doc) => doc.title.toLowerCase())
    .join(" " + Content.shared.t(`or`, { ns: ContentNS.docs }) + " ");
};

const getDescriptionForVerificationRequired = (
  docType: string
): [string, string] => {
  return [
    Content.shared.t(`verification-required.title`, {
      ns: ContentNS.errors,
    }),
    Content.shared.t(`verification-required.description`, {
      ns: ContentNS.errors,
      docType: docType,
    }),
  ];
};

const getDescriptionForRecognitionError = (
  error: PaxCheckRecognizeDocumentError,
  docType: string,
  provisionMethod: PaxCheckDocumentProvisionMethod
): [string, string] => {
  let fileTypes = "";
  if (error.type === "file-type-not-supported") {
    fileTypes = error.supportedFileTypes
      .map((fileType: string) =>
        fileType.substring(fileType.lastIndexOf("/") + 1)
      )
      .join(", ");
  }

  let maxFileSize = "";
  if (error.type === "file-too-large") {
    const size = error.maxSize >= GB ? error.maxSize / GB : error.maxSize / MB;
    const unit = error.maxSize >= GB ? "Gb" : "Mb";

    maxFileSize = Number.isInteger(size)
      ? `${size}${unit}`
      : `${size.toFixed(1)}${unit}`;
  }

  return [
    Content.shared.t("recognition-error.title", { ns: ContentNS.errors }),
    Content.shared.t(`recognition-error.${error.type}.${provisionMethod}`, {
      ns: ContentNS.errors,
      docType: docType,
      fileTypes: fileTypes,
      maxFileSize: maxFileSize,
    }),
  ];
};

// Fields for which we show a title that names the mismatched field (e.g.
// "There was an issue with the gender on your document") instead of the generic one.
const ID_MISMATCH_FIELDS_WITH_SPECIFIC_TITLE = [
  "givenName",
  "surname",
  "birthdate",
  "sex",
  "nationality",
] as const;

// Title naming a single mismatched field (e.g. "There was an issue with the
// gender on your document"). Falls back to the generic title for fields without a
// dedicated title and for tenants that don't define the field-specific keys
// (the generic title is the i18n `defaultValue`).
const idMismatchFieldTitle = (field: string): string => {
  const genericTitle = Content.shared.t("id-mismatch.title", {
    ns: ContentNS.errors,
  });

  if (
    !ID_MISMATCH_FIELDS_WITH_SPECIFIC_TITLE.includes(
      field as (typeof ID_MISMATCH_FIELDS_WITH_SPECIFIC_TITLE)[number]
    )
  ) {
    return genericTitle;
  }

  return Content.shared.t(`id-mismatch.title-${field}`, {
    ns: ContentNS.errors,
    defaultValue: genericTitle,
  });
};

// The single shared title (used by tenants that render all mismatches in one
// card). Names the field when it's the sole mismatch, otherwise stays generic.
const getIdMismatchTitle = (mismatches: PaxCheckIdMismatches): string => {
  const mismatchedFields = Object.entries(mismatches)
    .filter(([key]) => key !== "type")
    .filter(([, value]) => value != null)
    .map(([key]) => key);

  if (mismatchedFields.length !== 1) {
    return Content.shared.t("id-mismatch.title", { ns: ContentNS.errors });
  }

  return idMismatchFieldTitle(mismatchedFields[0]);
};

// Returns:
// string   - the single shared title (for one-card tenants)
// string[] - a description per mismatched field, plus a disclaimer
// string[] - the title for each description above (aligned by index), so
//            tenants that render one card per mismatch can name each field
const getDescriptionsForIdMismatches = (
  mismatches: PaxCheckIdMismatches,
  docType: string
): [string, string[], string[]] => {
  const genericTitle = Content.shared.t("id-mismatch.title", {
    ns: ContentNS.errors,
  });

  const disclaimer = Content.shared.t("id-mismatch.disclaimer", {
    ns: ContentNS.errors,
    docType: docType,
  });

  const getExpectedAndActualValues = (key: keyof PaxCheckIdMismatches) => {
    if (key === "givenName" || key === "surname" || key === "fullName") {
      const normalizedValues = mismatches[key]?.map((v) => normalizeName(v));
      return {
        expected: normalizedValues?.[0] ?? "",
        actual: normalizedValues?.[1] ?? "",
      };
    }

    return {
      expected: mismatches[key]?.[0] ?? "",
      actual: mismatches[key]?.[1] ?? "",
    };
  };

  const isAllFieldsEmpty = Object.entries(mismatches)
    .filter(([key]) => key !== "type")
    .every(([_, value]) => value == null); // checks null or undefined

  if (isAllFieldsEmpty) {
    const descriptions = [
      Content.shared.t("id-mismatch.default", {
        ns: ContentNS.errors,
        docType: docType,
      }),
      disclaimer,
    ];
    return [genericTitle, descriptions, descriptions.map(() => genericTitle)];
  }

  // Build description/title pairs together so the two arrays stay aligned.
  const fieldEntries = Object.entries(mismatches)
    .filter(([_, value]) => value !== undefined)
    .filter(([key]) => key !== "type")
    .map(([key]) => {
      const { expected, actual } = getExpectedAndActualValues(
        key as keyof PaxCheckIdMismatches
      );

      return {
        title: idMismatchFieldTitle(key),
        description: Content.shared.t(`id-mismatch.${key}`, {
          ns: ContentNS.errors,
          docType: docType,
          expectedValue: expected,
          docValue: actual,
          interpolation: { escapeValue: false },
        }),
      };
    });

  const entries = [
    ...fieldEntries,
    { title: genericTitle, description: disclaimer },
  ];

  return [
    getIdMismatchTitle(mismatches),
    entries.map((e) => e.description),
    entries.map((e) => e.title),
  ];
};

const getDescriptionsForRulesMismatches = (
  mismatches: PaxCheckRulesMismatch[],
  nationalityFromQuestions: string | undefined,
  docType: string
): [string, string[]] => {
  const nationalityTitle = (s: string) =>
    Content.shared.t(s, { ns: ContentNS.nationalities });

  return [
    Content.shared.t("rules-mismatch.title", { ns: ContentNS.errors }),
    mismatches
      .map((mismatch) => {
        switch (mismatch.type) {
          case "nationality-in-mismatch":
            return Content.shared.t("rules-mismatch.nationality", {
              ns: ContentNS.errors,
              docType: docType,
              docNationality: nationalityTitle(mismatch.actual),
              nationality: nationalityFromQuestions
                ? nationalityTitle(nationalityFromQuestions)
                : "",
            });
          case "expiry-date-gte-mismatch":
            return Content.shared.t("rules-mismatch.doc-expiry-date", {
              ns: ContentNS.errors,
              docType: docType,
              expiryDate: mismatch.expected,
              interpolation: { escapeValue: false },
            });
          default:
            return Content.shared.t("rules-mismatch-generic", {
              ns: ContentNS.errors,
            });
        }
      })
      .filter((m) => m !== ""),
  ];
};

const getDescriptionForProvidePassportFirst = () => {
  return Content.shared.t(`provide-passport-first`, {
    ns: ContentNS.errors,
  });
};

const getDescriptionForUnknownError = (): [string, string] => {
  return [
    Content.shared.t("unknown-error.title", { ns: ContentNS.errors }),
    Content.shared.t("unknown-error.description", { ns: ContentNS.errors }),
  ];
};

// returns:
// boolean - hasErrors
// string[] - errorDescriptions
// string? - errorTitle (single shared title)
// string[]? - a title per description (aligned by index), for tenants that
//             render one card per description and want to name each field
const useErrors = (
  allowedDocuments: AllowedDocument[]
): [boolean, string[], string?, string[]?] => {
  const paxSession = usePaxSession();

  const processingError = useAppStore((state) => state.processingError);
  const processedDocTypes = useAppStore((state) => state.processedDocTypes);
  const processingMode = useAppStore((state) => state.provisionMethod);

  const processedDocs = processedDocTypes?.flatMap((docType) => {
    return allowedDocuments.filter((doc) => doc.type === docType) || [];
  });

  const nationalityFromQuestions =
    paxSession?.checklist.nationality !== null
      ? paxSession?.checklist.nationality
      : undefined;

  // Do not show error if checklist is ready to fly
  if (
    paxSession?.checklist.resolutionStatus ===
    ChecklistResolutionStatus.ReadyToFly
  ) {
    return [false, []];
  }

  // Do not show error of certain kinds
  if (!processingError || processingError === "signals_mismatch") {
    return [false, []];
  }

  if (processingError === "unknown_error") {
    const [title, description] = getDescriptionForUnknownError();
    return [true, [description], title];
  }

  if (processingError === "verification_required") {
    const [title, description] = getDescriptionForVerificationRequired(
      documentTitle(processedDocs)
    );

    return [true, [description], title];
  }

  if (processingError instanceof ApplyDocumentErrorRecognitionFailed) {
    const [title, description] = getDescriptionForRecognitionError(
      processingError.recognitionError,
      documentTitle(processedDocs),
      processingMode
    );
    return [true, [description], title];
  }

  if (processingError instanceof ApplyDocumentErrorIdMismatch) {
    const [title, descriptions, descriptionTitles] =
      getDescriptionsForIdMismatches(
        processingError.idMismatches,
        documentTitle(processedDocs)
      );
    return [true, descriptions, title, descriptionTitles];
  }

  if (processingError instanceof ApplyDocumentErrorProvidePassportFirst) {
    return [true, [getDescriptionForProvidePassportFirst()]];
  }

  if (processingError instanceof ApplyDocumentErrorRulesMismatch) {
    const [title, descriptions] = getDescriptionsForRulesMismatches(
      processingError.rulesMismatches,
      nationalityFromQuestions,
      documentTitle(processedDocs)
    );
    return [true, descriptions, title];
  }

  if (processingError instanceof ApplyDocumentErrorNoApplicableItems) {
    const [title, description] = getDescriptionForUnknownError();
    return [true, [description], title];
  }

  const [title, description] = getDescriptionForUnknownError();
  return [true, [description], title];
};

export default useErrors;
